/* eslint-disable @scandipwa/scandipwa-guidelines/only-render-in-component */
/* eslint-disable react/no-unknown-property */
/* eslint-disable @scandipwa/scandipwa-guidelines/jsx-no-conditional */
/**
 * ScandiPWA - Progressive Web App for Magento
 *
 * Copyright © Scandiweb, Inc. All rights reserved.
 * See LICENSE for license details.
 *
 * @license OSL-3.0 (Open Software License ("OSL") v. 3.0)
 * @package scandipwa/base-theme
 * @link https://github.com/scandipwa/base-theme
 */

import PropTypes from 'prop-types';

import Breadcrumb from 'Component/Breadcrumb';
import Link from 'Component/Link';
import { Breadcrumbs as SourceBreadcrumbs } from 'SourceComponent/Breadcrumbs/Breadcrumbs.component';
import { DeviceType } from 'Type/Device.type';
import { appendWithStoreCode } from 'Util/Url';

import { DESKTOP_MIN_WIDTH } from './Breadcrumbs.config';

import './Breadcrumbs.style';

/** @namespace Scandipwa/Component/Breadcrumbs/Component */
export class BreadcrumbsComponent extends SourceBreadcrumbs {
    static propTypes = {
        ...super.propTypes,
        device: DeviceType.isRequired,
        search: PropTypes.string
    };

    static defaultProps = {
        search: ''
    };

    state = {
        isDesktop: false
    };

    updateWindowDimensions = this.updateWindowDimensions.bind(this);

    componentDidMount() {
        this.updateWindowDimensions();
        window.addEventListener('resize', this.updateWindowDimensions);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateWindowDimensions);
    }

    updateWindowDimensions() {
        const { isDesktop } = this.state;
        const newIsDesktop = (window.innerWidth >= DESKTOP_MIN_WIDTH);

        if (isDesktop !== newIsDesktop) {
            this.setState({ isDesktop: newIsDesktop });
        }
    }

    renderMobileBreadCrumb() {
        const { breadcrumbs } = this.props;
        const mobileBreadcrumb = breadcrumbs.length > 2
            ? breadcrumbs[breadcrumbs.length - 2] : breadcrumbs[breadcrumbs.length - 1];
        const { name, url } = mobileBreadcrumb || {};

        if (breadcrumbs.length === 1) {
            return null;
        }

        return (
            <Breadcrumb
              index={ 0 }
              name={ name }
              url={ url }
              isDisabled={ false }
            />
        );
    }

    renderDesktopBreadcrumbs() {
        const { breadcrumbs } = this.props;

        return breadcrumbs.length
            ? this.renderBreadcrumbList(breadcrumbs)
            : this.renderBreadcrumb({}, 0);
    }

    renderHomeBreadcrumb() {
        const { breadcrumbs, search } = this.props;

        if (!breadcrumbs.length && !search) {
            return null;
        }

        return (
            <Link
              block="Breadcrumbs"
              elem="Home"
              mods={ { isNotHomeBreadcrumb: (!search && breadcrumbs.length > 1) } }
              to="/"
            >
                <span>{ __('Home') }</span>
            </Link>
        );
    }

    renderBreadcrumbs() {
        const { isDesktop } = this.state;
        const { search } = this.props;

        if (search) {
            return (
                <div
                  block="Breadcrumbs"
                  elem="SearchResults"
                >
                    <span>{ `${ __('Search results for:') } '${ search }'` }</span>
                </div>
            );
        }

        return (
            <ul
              block="Breadcrumbs"
              elem="List"
              itemScope
              itemType="http://schema.org/BreadcrumbList"
            >
                { !isDesktop ? this.renderMobileBreadCrumb() : this.renderDesktopBreadcrumbs() }
            </ul>
        );
    }

    renderBreadcrumbList(breadcrumbs) {
        return breadcrumbs.map((_, i) => this.renderBreadcrumb(
            breadcrumbs[breadcrumbs.length - 1 - i], i
        ));
    }

    renderBreadcrumb({ url, name, category_id }, i) {
        const { breadcrumbs } = this.props;
        const isDisabled = !url || breadcrumbs.length - 1 === i;

        return (
            <Breadcrumb
              name={ name }
              url={ url }
              index={ i }
              key={ i }
              isDisabled={ isDisabled }
              category_id={ category_id }
            />
        );
    }

    render() {
        const { areBreadcrumbsVisible } = this.props;

        if (
            !areBreadcrumbsVisible
            || location.pathname === appendWithStoreCode('/')
            || location.pathname === '/'
        ) {
            return null;
        }

        return (
            <nav
              block="Breadcrumbs"
              aria-label="Breadcrumbs navigation"
            >
                { this.renderHomeBreadcrumb() }
                { this.renderBreadcrumbs() }
            </nav>
        );
    }
}

export default BreadcrumbsComponent;
